import { useMemo, useState } from 'react';
import { Alert, Button, Badge, Input } from '@dreamtree-org/twreact-ui';
import { Download } from 'lucide-react';
import { PageHeader, SectionCard, QueryState, EmptyState, StatCard, Money } from '@/components';
import { formatINR } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import { BarChart } from '@dreamtree-org/twreact-ui/charts';
import { reconcile, totalOfKind, RECON_STATUS, EVENT_KIND } from '../lib/reconcile';
import { filterRows, optionsWithCounts } from '../lib/filters';
import { toCsv, downloadText, paiseToRupees } from '../lib/csv';
import { formatDateTime, formatINRCompact, humanize } from '../lib/format';

const CSV_COLUMNS = [
  { key: 'status', label: 'Status', value: (r) => RECON_STATUS[r.status].label },
  { key: 'kind', label: 'Kind' },
  { key: 'merchant_slug', label: 'Merchant' },
  { key: 'order_ref', label: 'Order' },
  { key: 'provider_id', label: 'Provider reference' },
  { key: 'ledger_id', label: 'Ledger entry' },
  { key: 'provider_paise', label: 'Provider amount (INR)', value: (r) => paiseToRupees(r.provider_paise) },
  { key: 'ledger_paise', label: 'Ledger amount (INR)', value: (r) => paiseToRupees(r.ledger_paise) },
  { key: 'delta_paise', label: 'Delta (INR)', value: (r) => paiseToRupees(r.delta_paise) },
  { key: 'at', label: 'Timestamp (UTC)' },
];

/** ADMIN-04 Payment reconciliation (real since D8a): provider events vs each store's ledger, per month. */
export default function Reconciliation() {
  const [period, setPeriod] = useState('');
  const query = useQuery('platformReconciliation', { period: period || undefined }, { select: (r) => r.data });
  const [q, setQ] = useState('');
  const [f, setF] = useState({ status: 'open', kind: 'all', merchant_slug: 'all' });
  const result = useMemo(() => (query.data ? reconcile(query.data.events, query.data.entries) : null), [query.data]);

  return (
    <div className="space-y-6">
      <PageHeader title="Payment reconciliation" subtitle="ADMIN-04 · provider events vs the platform ledger, transfers, refunds and commission" />
      <QueryState query={query}>
        {(d) => {
          const { rows, totals } = result;
          const scoped = f.status === 'open' ? rows.filter((r) => r.status !== 'matched') : rows;
          const list = filterRows(scoped, { status: f.status === 'open' ? '' : f.status, kind: f.kind, merchant_slug: f.merchant_slug }, q, ['order_ref', 'provider_id', 'ledger_id', 'merchant_slug']);
          const exportCsv = () => downloadText(`reconciliation-${d.period.replace(/\s+/g, '-').toLowerCase()}.csv`, toCsv(list, CSV_COLUMNS));
          const byKind = Object.values(EVENT_KIND).map((kind) => ({
            label: humanize(kind),
            value: d.events.filter((e) => EVENT_KIND[e.type] === kind).reduce((a, e) => a + e.amount_paise, 0),
          }));
          const webhookIssues = d.events.filter((e) => e.webhook_status !== 'delivered');
          return (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline">{d.provider}</Badge>
                <Badge variant="outline">{d.period}</Badge>
                <span className="opacity-60">as of {formatDateTime(d.as_of)}</span>
                <span className="ml-auto w-44"><Input type="month" aria-label="Month" value={period || d.period_key} onChange={(e) => setPeriod(e.target.value)} /></span>
              </div>
              {d.unreachable && d.unreachable.length ? <Alert variant="warning">Ledgers of {d.unreachable.join(', ')} could not be read; their rows are missing from this report.</Alert> : null}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <StatCard label="Provider total" value={formatINR(totals.provider)} hint={`${d.events.length} events`} />
                <StatCard label="Ledger total" value={formatINR(totals.ledger)} hint="excl. commission" />
                <StatCard label="Matched" value={formatINR(totals.matched)} hint={`${rows.length - totals.openCount} lines`} />
                <StatCard label="Missing in ledger" value={formatINR(totals.unmatchedProvider)} hint="provider only" />
                <StatCard label="Missing at provider" value={formatINR(totals.unmatchedLedger)} hint="ledger only" />
                <StatCard label="Amount mismatches" value={formatINR(totals.mismatchDelta)} hint={`${totals.openCount} open lines`} />
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <SectionCard title="Provider volume by type">
                  <BarChart title="Provider volume by type" data={byKind} index="label" categories={['value']} valueFormatter={formatINRCompact} showLegend={false} yAxisWidth={64} height={200} />
                </SectionCard>
                <SectionCard title="Platform commission">
                  <div className="text-2xl font-bold">{formatINR(totalOfKind(d.entries, 'commission'))}</div>
                  <p className="text-sm opacity-70">Booked on {d.entries.filter((e) => e.kind === 'commission').length} orders this period (commission rules v3).</p>
                </SectionCard>
                <SectionCard title="Webhook delivery">
                  {webhookIssues.length ? (
                    <ul className="space-y-2 text-sm">
                      {webhookIssues.map((e) => (
                        <li key={e.id} className="flex items-center justify-between gap-2">
                          <code className="text-xs break-all">{e.id}</code>
                          <Badge variant="warning" size="sm">{e.webhook_status}</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm opacity-70">All provider webhooks delivered.</p>}
                </SectionCard>
              </div>

              <SectionCard
                title="Reconciliation lines"
                actions={<Button variant="outline" leftIcon={<Download size={16} />} onClick={exportCsv} disabled={!list.length}>Export CSV ({list.length})</Button>}
              >
                <FilterBar
                  q={q}
                  onQ={setQ}
                  placeholder="Order, provider id, ledger id…"
                  filters={[
                    { key: 'status', label: 'Status', value: f.status, options: [{ value: 'open', label: `Unmatched (${totals.openCount})` }, ...optionsWithCounts(rows, 'status', (s) => RECON_STATUS[s].label)] },
                    { key: 'kind', label: 'Kind', value: f.kind, options: optionsWithCounts(rows, 'kind') },
                    { key: 'merchant_slug', label: 'Merchant', value: f.merchant_slug, options: optionsWithCounts(rows, 'merchant_slug') },
                  ]}
                  onFilter={(k, v) => setF((x) => ({ ...x, [k]: v }))}
                />
                {list.length ? (
                  <DataTable
                    data={list}
                    pagination={list.length > 25}
                    columns={[
                      { key: 'status', label: 'Status', render: (r) => <Badge variant={RECON_STATUS[r.status].variant} size="sm">{RECON_STATUS[r.status].label}</Badge> },
                      { key: 'order_ref', label: 'Order', render: (r) => (
                        <div><div className="font-medium">{r.order_ref}</div><div className="text-xs opacity-60">{humanize(r.kind)} · {r.merchant_slug}</div></div>
                      ) },
                      { key: 'provider_id', label: 'Provider ref', hideOnMobile: true, render: (r) => <code className="text-xs">{r.provider_id || '—'}</code> },
                      { key: 'ledger_id', label: 'Ledger', hideOnMobile: true, render: (r) => <code className="text-xs">{r.ledger_id || '—'}</code> },
                      { key: 'provider_paise', label: 'Provider', render: (r) => (r.provider_paise == null ? '—' : <Money paise={r.provider_paise} />) },
                      { key: 'ledger_paise', label: 'Ledger', render: (r) => (r.ledger_paise == null ? '—' : <Money paise={r.ledger_paise} />) },
                      { key: 'delta_paise', label: 'Delta', hideOnMobile: true, render: (r) => (r.delta_paise ? <span className="text-error-600 font-medium">{formatINR(r.delta_paise)}</span> : '—') },
                      { key: 'at', label: 'When', hideOnMobile: true, render: (r) => formatDateTime(r.at) },
                    ]}
                  />
                ) : <EmptyState title="Nothing to reconcile">Every line in this filter is matched.</EmptyState>}
              </SectionCard>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
